/* eslint-disable react-hooks/exhaustive-deps */
import React,{useEffect,useState} from 'react';
import {useNavigate,useLocation,useSearchParams} from 'react-router-dom';
import Layout from '../components/layout/Layout';
import DataTable from '../components/common/DataTable';
import {Input,Select,Textarea} from '../components/common/FormField';
import api from '../services/api';
import {fmtDateTime} from '../utils/helpers';
import {prettyMortuary} from '../utils/mortuaryLabels';

const unwrap=(r:any)=>r?.data?.data??r?.data??r;
const TABS=['chapel','transport','cemetery','crematorium','funeral','portal'] as const;
const TAB_LABEL:Record<(typeof TABS)[number],string>={
  chapel:'Chapel',
  transport:'Transport',
  cemetery:'Cemetery / burial',
  crematorium:'Crematorium',
  funeral:'Funeral directors',
  portal:'Family portal',
};
const PATH_TAB:Record<string,(typeof TABS)[number]>={
  '/mortuary/chapel':'chapel',
  '/mortuary/transport':'transport',
  '/mortuary/cemetery':'cemetery',
  '/mortuary/crematorium':'crematorium',
  '/mortuary/funeral':'funeral',
  '/mortuary/final-disposition':'crematorium',
  '/mortuary/services':'chapel',
};
const TAB_PATH:Record<(typeof TABS)[number],string>={
  chapel:'/mortuary/chapel',
  transport:'/mortuary/transport',
  cemetery:'/mortuary/cemetery',
  crematorium:'/mortuary/crematorium',
  funeral:'/mortuary/funeral',
  portal:'/mortuary/services',
};

export default function MortuaryServicesPage(){
  const nav=useNavigate();
  const loc=useLocation();
  const [params]=useSearchParams();
  const qTab=params.get('tab');
  const bodyId=params.get('bodyId')||'';
  const initialTab=(qTab&&(TABS as readonly string[]).includes(qTab)?qTab:PATH_TAB[loc.pathname]||'chapel') as (typeof TABS)[number];
  const [tab,setTab]=useState<(typeof TABS)[number]>(initialTab);
  const [msg,setMsg]=useState('');
  const [bodies,setBodies]=useState<any[]>([]);
  const [chapels,setChapels]=useState<any[]>([]);
  const [resources,setResources]=useState<any[]>([]);
  const [board,setBoard]=useState<any[]>([]);
  const [directors,setDirectors]=useState<any[]>([]);
  const [vehicles,setVehicles]=useState<any[]>([]);
  const [drivers,setDrivers]=useState<any[]>([]);
  const [trips,setTrips]=useState<any[]>([]);
  const [cemeteries,setCemeteries]=useState<any[]>([]);
  const [plots,setPlots]=useState<any[]>([]);
  const [burials,setBurials]=useState<any[]>([]);
  const [units,setUnits]=useState<any[]>([]);
  const [cremations,setCremations]=useState<any[]>([]);
  const [dispositions,setDispositions]=useState<any[]>([]);
  const [chapelForm,setChapelForm]=useState<any>({serviceType:'FAMILY_VIEWING',bodyId});
  const [tripForm,setTripForm]=useState<any>({purpose:'TRANSFER',bodyId});
  const [burialForm,setBurialForm]=useState<any>({bodyId});
  const [cremForm,setCremForm]=useState<any>({bodyId});
  const [fdForm,setFdForm]=useState<any>({bodyId});
  const [fleetForm,setFleetForm]=useState<any>({});
  const [portalForm,setPortalForm]=useState<any>({kind:'FAMILY',bodyId});
  const [portalUrl,setPortalUrl]=useState('');

  const notify=(m:string)=>setMsg(m);
  const err=(e:any)=>notify('❌ '+(e.response?.data?.message||e.message||'Request failed'));

  const load=async()=>{
    const [b,c,r,bd,fd,v,d,t,cem,p,bur,u,cr,disp]=await Promise.all([
      api.getMortuaryBodies({take:100}),
      api.getMortuaryChapels(),
      api.getMortuaryChapelResources(),
      api.getMortuaryChapelBoard(),
      api.getMortuaryFuneralDirectors(),
      api.getMortuaryVehicles(),
      api.getMortuaryDrivers(),
      api.getMortuaryTrips(),
      api.getMortuaryCemeteries(),
      api.getMortuaryPlots(),
      api.getMortuaryBurials(),
      api.getMortuaryCrematoriumUnits(),
      api.getMortuaryCremations(),
      api.getMortuaryDispositions(),
    ]);
    const body=unwrap(b);
    setBodies(body?.data||body||[]);
    setChapels(unwrap(c)||[]);setResources(unwrap(r)||[]);setBoard(unwrap(bd)||[]);
    setDirectors(unwrap(fd)||[]);setVehicles(unwrap(v)||[]);setDrivers(unwrap(d)||[]);
    setTrips(unwrap(t)||[]);setCemeteries(unwrap(cem)||[]);setPlots(unwrap(p)||[]);
    setBurials(unwrap(bur)||[]);setUnits(unwrap(u)||[]);setCremations(unwrap(cr)||[]);
    setDispositions(unwrap(disp)||[]);
  };
  useEffect(()=>{load().catch(err);},[]);
  useEffect(()=>{
    const next=(qTab&&(TABS as readonly string[]).includes(qTab)?qTab:PATH_TAB[loc.pathname]||'chapel') as (typeof TABS)[number];
    setTab(next);
    if(bodyId){
      setChapelForm((f:any)=>({...f,bodyId}));
      setTripForm((f:any)=>({...f,bodyId}));
      setBurialForm((f:any)=>({...f,bodyId}));
      setCremForm((f:any)=>({...f,bodyId}));
      setFdForm((f:any)=>({...f,bodyId}));
      setPortalForm((f:any)=>({...f,bodyId}));
    }
  },[loc.pathname,qTab,bodyId]);

  const goTab=(id:(typeof TABS)[number])=>{
    setTab(id);
    const next=new URLSearchParams();
    if(bodyId) next.set('bodyId',bodyId);
    if(id==='portal') next.set('tab','portal');
    const qs=next.toString();
    nav(`${TAB_PATH[id]}${qs?`?${qs}`:''}`,{replace:true});
  };

  const bodyOpts=bodies.map((x:any)=><option key={x.id} value={x.id}>{x.body_number} · {x.full_name||'Unknown'}</option>);

  return(
    <Layout>
      <div className="space-y-4">
        <div className="flex flex-wrap justify-between gap-2">
          <div>
            <button className="text-xs text-[#02357A]" onClick={()=>nav('/mortuary')}>← Command centre</button>
            <h1 className="text-xl font-bold">Chapel, funeral & final disposition</h1>
            <p className="text-xs text-slate-500">Book a viewing, send a hearse, or record burial / cremation. Double-bookings are blocked automatically.</p>
          </div>
        </div>
        {msg&&<div className="text-sm bg-slate-50 border rounded-lg px-3 py-2">{msg}</div>}
        <div className="flex flex-wrap gap-2">
          {TABS.map(id=>(
            <button key={id} onClick={()=>goTab(id)} className={`text-xs px-3 py-1.5 rounded-full border ${tab===id?'bg-[#02357A] text-white border-[#015CA6]':'border-slate-300 text-slate-600'}`}>{TAB_LABEL[id]}</button>
          ))}
        </div>

        {tab==='chapel'&&(
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="card p-4 space-y-2 lg:col-span-1">
              <h3 className="font-semibold">Book chapel / viewing</h3>
              <Select value={chapelForm.bodyId||''} onChange={e=>setChapelForm({...chapelForm,bodyId:e.target.value})}><option value="">Body (optional)</option>{bodyOpts}</Select>
              <Select value={chapelForm.chapelId||''} onChange={e=>setChapelForm({...chapelForm,chapelId:e.target.value})}><option value="">Chapel / room</option>{chapels.map((c:any)=><option key={c.id} value={c.id}>{c.name} ({c.kind})</option>)}</Select>
              <Select value={chapelForm.serviceType} onChange={e=>setChapelForm({...chapelForm,serviceType:e.target.value})}>
                {['PRIVATE_VIEWING','FAMILY_VIEWING','PRAYER','RELIGIOUS_CEREMONY','MEMORIAL','FUNERAL_SERVICE','VIGIL','NON_RELIGIOUS_SERVICE'].map(s=><option key={s} value={s}>{prettyMortuary(s)}</option>)}
              </Select>
              <Input type="datetime-local" value={chapelForm.startsAt||''} onChange={e=>setChapelForm({...chapelForm,startsAt:e.target.value})}/>
              <Input type="datetime-local" value={chapelForm.endsAt||''} onChange={e=>setChapelForm({...chapelForm,endsAt:e.target.value})}/>
              <Select value={chapelForm.resourceId||''} onChange={e=>setChapelForm({...chapelForm,resourceId:e.target.value})}><option value="">Resource (optional)</option>{resources.map((r:any)=><option key={r.id} value={r.id}>{r.name}</option>)}</Select>
              <Input placeholder="Celebrant / clergy" value={chapelForm.celebrant||''} onChange={e=>setChapelForm({...chapelForm,celebrant:e.target.value})}/>
              <Textarea rows={2} placeholder="Family preferences (optional)" value={chapelForm.familyPreferences||''} onChange={e=>setChapelForm({...chapelForm,familyPreferences:e.target.value})}/>
              <button className="btn-primary text-sm" onClick={async()=>{
                try{
                  await api.bookMortuaryChapel({...chapelForm,resourceIds:chapelForm.resourceId?[chapelForm.resourceId]:[]});
                  notify('✅ Chapel reserved');await load();
                }catch(e){err(e);}
              }}>Reserve</button>
            </div>
            <div className="lg:col-span-2 card p-4">
              <h3 className="font-semibold mb-2">Live board</h3>
              <DataTable data={board} emptyTitle="No chapel bookings"
                columns={[
                  {key:'starts_at',label:'Time',render:(r:any)=>fmtDateTime(r.starts_at)},
                  {key:'chapel_name',label:'Chapel'},
                  {key:'service_type',label:'Service',render:(r:any)=>prettyMortuary(r.service_type)},
                  {key:'body_number',label:'Body'},
                  {key:'status',label:'Status',render:(r:any)=>prettyMortuary(r.status)},
                  {key:'id',label:'',render:(r:any)=>(
                    <span className="flex gap-2">
                      {r.status==='RESERVED'&&<button className="text-xs underline" onClick={async()=>{await api.confirmMortuaryChapel(r.id);load();}}>Confirm</button>}
                      {['RESERVED','CONFIRMED','IN_PROGRESS'].includes(r.status)&&<button className="text-xs underline" onClick={async()=>{await api.completeMortuaryChapel(r.id);load();}}>Complete</button>}
                      {r.status!=='CANCELLED'&&r.status!=='COMPLETED'&&<button className="text-xs underline text-red-700" onClick={async()=>{await api.cancelMortuaryChapel(r.id);load();}}>Cancel</button>}
                    </span>
                  )},
                ]}/>
            </div>
          </div>
        )}

        {tab==='transport'&&(
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="card p-4 space-y-2">
              <h3 className="font-semibold">Fleet</h3>
              <Input placeholder="Vehicle registration" value={fleetForm.registration||''} onChange={e=>setFleetForm({...fleetForm,registration:e.target.value})}/>
              <Input placeholder="Vehicle type (HEARSE)" value={fleetForm.kind||''} onChange={e=>setFleetForm({...fleetForm,kind:e.target.value})}/>
              <button className="btn-secondary text-sm" onClick={async()=>{try{await api.saveMortuaryVehicle(fleetForm);notify('✅ Vehicle saved');await load();}catch(e){err(e);}}}>Save vehicle</button>
              <Input placeholder="Driver name" value={fleetForm.driverName||''} onChange={e=>setFleetForm({...fleetForm,driverName:e.target.value})}/>
              <Input placeholder="Driver phone" value={fleetForm.driverPhone||''} onChange={e=>setFleetForm({...fleetForm,driverPhone:e.target.value})}/>
              <button className="btn-secondary text-sm" onClick={async()=>{try{await api.saveMortuaryDriver({name:fleetForm.driverName,phone:fleetForm.driverPhone});notify('✅ Driver saved');await load();}catch(e){err(e);}}}>Save driver</button>
              <h3 className="font-semibold pt-2">Schedule trip</h3>
              <Select value={tripForm.bodyId||''} onChange={e=>setTripForm({...tripForm,bodyId:e.target.value})}><option value="">Body</option>{bodyOpts}</Select>
              <Select value={tripForm.vehicleId||''} onChange={e=>setTripForm({...tripForm,vehicleId:e.target.value})}><option value="">Vehicle</option>{vehicles.map((v:any)=><option key={v.id} value={v.id}>{v.registration}</option>)}</Select>
              <Select value={tripForm.driverId||''} onChange={e=>setTripForm({...tripForm,driverId:e.target.value})}><option value="">Driver</option>{drivers.map((d:any)=><option key={d.id} value={d.id}>{d.name}</option>)}</Select>
              <Select value={tripForm.purpose} onChange={e=>setTripForm({...tripForm,purpose:e.target.value})}>{['TRANSFER','PICKUP','CHAPEL','CEMETERY','CREMATORIUM'].map(s=><option key={s} value={s}>{prettyMortuary(s)}</option>)}</Select>
              <Input placeholder="Pickup" value={tripForm.pickup||''} onChange={e=>setTripForm({...tripForm,pickup:e.target.value})}/>
              <Input placeholder="Destination" value={tripForm.destination||''} onChange={e=>setTripForm({...tripForm,destination:e.target.value})}/>
              <Input type="datetime-local" value={tripForm.startsAt||''} onChange={e=>setTripForm({...tripForm,startsAt:e.target.value})}/>
              <button className="btn-primary text-sm" onClick={async()=>{try{await api.scheduleMortuaryTrip(tripForm);notify('✅ Trip scheduled');await load();}catch(e){err(e);}}}>Schedule</button>
            </div>
            <div className="lg:col-span-2 card p-4">
              <DataTable data={trips} emptyTitle="No trips"
                columns={[
                  {key:'trip_number',label:'Trip'},{key:'registration',label:'Vehicle'},{key:'driver_name',label:'Driver'},
                  {key:'starts_at',label:'Start',render:(r:any)=>fmtDateTime(r.starts_at)},{key:'status',label:'Status',render:(r:any)=>prettyMortuary(r.status)},
                  {key:'id',label:'',render:(r:any)=>r.status==='SCHEDULED'?<button className="text-xs underline" onClick={async()=>{await api.dispatchMortuaryTrip(r.id);load();}}>Dispatch</button>:r.status==='DISPATCHED'?<button className="text-xs underline" onClick={async()=>{await api.arriveMortuaryTrip(r.id);load();}}>Arrive</button>:null},
                ]}/>
            </div>
          </div>
        )}

        {tab==='cemetery'&&(
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="card p-4 space-y-2">
              <h3 className="font-semibold">Schedule burial</h3>
              <Select value={burialForm.bodyId||''} onChange={e=>setBurialForm({...burialForm,bodyId:e.target.value})}><option value="">Body</option>{bodyOpts}</Select>
              <Select value={burialForm.plotId||''} onChange={e=>setBurialForm({...burialForm,plotId:e.target.value})}><option value="">Plot</option>{plots.filter((p:any)=>p.status==='AVAILABLE').map((p:any)=><option key={p.id} value={p.id}>{[p.section,p.block,p.plot_code,p.grave_code].filter(Boolean).join(' / ')}</option>)}</Select>
              <Input type="datetime-local" value={burialForm.scheduledAt||''} onChange={e=>setBurialForm({...burialForm,scheduledAt:e.target.value})}/>
              <Input placeholder="Override reason (required if legal hold)" value={burialForm.overrideReason||''} onChange={e=>setBurialForm({...burialForm,overrideReason:e.target.value})}/>
              <button className="btn-primary text-sm" onClick={async()=>{try{await api.scheduleMortuaryBurial(burialForm);notify('✅ Burial reserved');await load();}catch(e){err(e);}}}>Reserve plot</button>
              <p className="text-xs text-slate-500">{cemeteries.map((c:any)=>`${c.name}: ${c.available_plots} free`).join(' · ')}</p>
            </div>
            <div className="lg:col-span-2 card p-4">
              <DataTable data={burials} emptyTitle="No burials"
                columns={[
                  {key:'burial_number',label:'Burial'},{key:'body_number',label:'Body'},{key:'plot_code',label:'Plot'},{key:'section',label:'Section'},{key:'block',label:'Block'},
                  {key:'scheduled_at',label:'When',render:(r:any)=>fmtDateTime(r.scheduled_at)},{key:'status',label:'Status'},
                  {key:'id',label:'',render:(r:any)=>r.status==='SCHEDULED'&&<button className="text-xs underline" onClick={async()=>{await api.completeMortuaryBurial(r.id);load();}}>Complete</button>},
                ]}/>
            </div>
          </div>
        )}

        {tab==='crematorium'&&(
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="card p-4 space-y-2">
              <h3 className="font-semibold">Book cremation</h3>
              <Select value={cremForm.bodyId||''} onChange={e=>setCremForm({...cremForm,bodyId:e.target.value})}><option value="">Body</option>{bodyOpts}</Select>
              <Select value={cremForm.unitId||''} onChange={e=>setCremForm({...cremForm,unitId:e.target.value})}><option value="">Unit</option>{units.map((u:any)=><option key={u.id} value={u.id}>{u.unit_code}</option>)}</Select>
              <Input type="datetime-local" value={cremForm.startsAt||''} onChange={e=>setCremForm({...cremForm,startsAt:e.target.value})}/>
              <Input type="datetime-local" value={cremForm.endsAt||''} onChange={e=>setCremForm({...cremForm,endsAt:e.target.value})}/>
              <Input placeholder="Authorization ref" value={cremForm.authorizationRef||''} onChange={e=>setCremForm({...cremForm,authorizationRef:e.target.value})}/>
              <Input placeholder="Override reason (required if legal hold)" value={cremForm.overrideReason||''} onChange={e=>setCremForm({...cremForm,overrideReason:e.target.value})}/>
              <button className="btn-primary text-sm" onClick={async()=>{try{await api.scheduleMortuaryCremation(cremForm);notify('✅ Slot reserved');await load();}catch(e){err(e);}}}>Reserve slot</button>
            </div>
            <div className="lg:col-span-2 card p-4">
              <DataTable data={cremations} emptyTitle="No cremations"
                columns={[
                  {key:'cremation_number',label:'Cremation'},{key:'body_number',label:'Body'},{key:'unit_code',label:'Unit'},
                  {key:'starts_at',label:'Slot',render:(r:any)=>fmtDateTime(r.starts_at)},{key:'status',label:'Status'},{key:'urn_number',label:'Urn'},
                  {key:'id',label:'',render:(r:any)=>r.status!=='COMPLETED'&&<button className="text-xs underline" onClick={async()=>{await api.completeMortuaryCremation(r.id);load();}}>Complete</button>},
                ]}/>
              <h3 className="font-semibold mt-4 mb-2">Final dispositions</h3>
              <DataTable data={dispositions} emptyTitle="None" columns={[{key:'disposition_number',label:'No'},{key:'body_number',label:'Body'},{key:'kind',label:'Kind'},{key:'destination',label:'Destination'}]}/>
            </div>
          </div>
        )}

        {tab==='funeral'&&(
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="card p-4 space-y-2">
              <h3 className="font-semibold">Funeral director</h3>
              <Input placeholder="Code" value={fdForm.code||''} onChange={e=>setFdForm({...fdForm,code:e.target.value})}/>
              <Input placeholder="Name" value={fdForm.name||''} onChange={e=>setFdForm({...fdForm,name:e.target.value})}/>
              <Input placeholder="Organization" value={fdForm.organization||''} onChange={e=>setFdForm({...fdForm,organization:e.target.value})}/>
              <Input placeholder="Phone" value={fdForm.phone||''} onChange={e=>setFdForm({...fdForm,phone:e.target.value})}/>
              <button className="btn-primary text-sm" onClick={async()=>{try{await api.saveMortuaryFuneralDirector(fdForm);notify('✅ Director saved');await load();}catch(e){err(e);}}}>Save director</button>
              <Select value={fdForm.bodyId||''} onChange={e=>setFdForm({...fdForm,bodyId:e.target.value})}><option value="">Assign to body</option>{bodyOpts}</Select>
              <Select value={fdForm.directorId||''} onChange={e=>setFdForm({...fdForm,directorId:e.target.value})}><option value="">Director</option>{directors.map((d:any)=><option key={d.id} value={d.id}>{d.name}</option>)}</Select>
              <button className="btn-secondary text-sm" onClick={async()=>{if(!fdForm.bodyId||!fdForm.directorId)return;await api.assignMortuaryFuneralDirector(fdForm.bodyId,fdForm.directorId);notify('✅ Assigned');}}>Assign</button>
            </div>
            <div className="card p-4">
              <DataTable data={directors} emptyTitle="No funeral directors" columns={[{key:'code',label:'Code'},{key:'name',label:'Name'},{key:'organization',label:'Org'},{key:'phone',label:'Phone'}]}/>
            </div>
          </div>
        )}

        {tab==='portal'&&(
          <div className="card p-4 space-y-2 max-w-xl">
            <h3 className="font-semibold">Issue scoped portal access</h3>
            <p className="text-xs text-slate-500">Family and funeral-director tokens never include autopsy, evidence or chain-of-custody data.</p>
            <Select value={portalForm.bodyId||''} onChange={e=>setPortalForm({...portalForm,bodyId:e.target.value})}><option value="">Body</option>{bodyOpts}</Select>
            <Select value={portalForm.kind} onChange={e=>setPortalForm({...portalForm,kind:e.target.value})}><option>FAMILY</option><option>FUNERAL_DIRECTOR</option></Select>
            <button className="btn-primary text-sm" onClick={async()=>{
              if(!portalForm.bodyId) return;
              try{
                const r=unwrap(await api.inviteMortuaryPortal(portalForm.bodyId,{kind:portalForm.kind}));
                const url=`${window.location.origin}/mortuary-portal?token=${r.token}`;
                setPortalUrl(url);notify('✅ Portal token issued');
              }catch(e){err(e);}
            }}>Issue token</button>
            {portalUrl&&<p className="text-xs break-all">Share: {portalUrl}</p>}
          </div>
        )}
      </div>
    </Layout>
  );
}
